import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Loader2, Send } from 'lucide-react';

const CATEGORIES = [
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'ux_improvement', label: 'UX Improvement' },
  { value: 'general', label: 'General Feedback' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-green-400' },
  { value: 'medium', label: 'Medium', color: 'text-amber-400' },
  { value: 'high', label: 'High', color: 'text-red-400' },
];

const FeedbackDialog = ({ open, onOpenChange }) => {
  const { api } = useAuth();
  const location = useLocation();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    category: 'general',
    subject: '',
    message: '',
    priority: 'medium',
  });

  const resetForm = () => {
    setForm({ category: 'general', subject: '', message: '', priority: 'medium' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/feedback', {
        ...form,
        page_context: location.pathname,
      });
      const ticketNum = res.data?.ticket_number;
      toast.success(
        ticketNum
          ? `Feedback submitted as ${ticketNum} — track it in Support Tickets`
          : 'Feedback sent! Thank you for helping us improve.'
      );
      resetForm();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] bg-[#1a1a2e] border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#ffd108]/15">
              <Send className="h-3.5 w-3.5 text-[#ffd108]" />
            </span>
            Early Adopter Feedback
          </DialogTitle>
          <p className="text-sm text-slate-400 mt-1">
            Your feedback creates a support ticket so we can respond directly.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                <SelectTrigger
                  data-testid="feedback-category-select"
                  className="bg-slate-800/60 border-slate-600 text-white h-9"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">How urgent does this feel?</Label>
              <Select value={form.priority} onValueChange={(v) => setForm(p => ({ ...p, priority: v }))}>
                <SelectTrigger
                  data-testid="feedback-priority-select"
                  className="bg-slate-800/60 border-slate-600 text-white h-9"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className={p.color}>{p.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Subject</Label>
            <Input
              data-testid="feedback-subject-input"
              placeholder="Brief summary of your feedback"
              value={form.subject}
              onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))}
              className="bg-slate-800/60 border-slate-600 text-white h-9"
              maxLength={200}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">Message</Label>
            <Textarea
              data-testid="feedback-message-input"
              placeholder="Tell us more... What's working? What could be better?"
              value={form.message}
              onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))}
              className="bg-slate-800/60 border-slate-600 text-white min-h-[120px] resize-none"
              maxLength={5000}
            />
            <p className="text-xs text-slate-500 text-right">{form.message.length}/5000</p>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sending || !form.subject.trim() || !form.message.trim()}
              data-testid="feedback-submit-btn"
              className="bg-[#ffd108] text-black hover:bg-[#e6bc07] font-semibold"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Feedback
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackDialog;
